import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CommitStrategy, useScribe, type WordTimestamp } from "@elevenlabs/react";
import { Waveform } from "./components/Waveform";
import {
  LatencyTimeline,
  type TimelineEvent,
  type TimelineEventKind,
} from "./components/LatencyTimeline";
import { useAudioMeter } from "./hooks/use-audio-meter";
import { MicMark, StopMark, SwapMark } from "./icons";
import {
  getElevenLabsToken,
  getStackStatus,
  streamTranslation,
  type ProviderId,
  type ProviderStatus,
  type StackStatus,
} from "./lib/api";
import {
  AssemblyAITranscriptionSession,
  GeminiTranslationSession,
  GradiumTranslationSession,
  OpenAITranscriptionSession,
  OpenAITranslationSession,
  SonioxTranslationSession,
  type AssemblyAIMode,
  type NativeCaptionSnapshot,
  type NativeTranslationSession,
} from "./lib/native-providers";
import {
  completeWordsOnly,
  emptyCaptionRevision,
  splitStableCaption,
  type CaptionRevision,
} from "./lib/caption-revisions";
import { isLikelySilenceArtifact, resetVoiceEvidence, type VoiceEvidence } from "./lib/speech-evidence";
import { languages, languageName, type LanguageCode } from "../shared/languages";

type SessionState = "idle" | "starting" | "listening" | "stopping" | "error";

interface CaptionMetrics {
  firstSourcePartialMs?: number;
  firstTranslatedTextMs?: number;
  committedSourceMs?: number;
  finalCaptionAfterSpeechMs?: number;
  revisionCount: number;
}

interface CaptionRow {
  id: string;
  text: string;
  status: "settling" | "complete" | "error";
}

interface LaneState {
  sessionState: SessionState;
  settledSource: string[];
  sourcePreview: string;
  captionRows: CaptionRow[];
  captionPreview: CaptionRevision;
  metrics: CaptionMetrics;
  error: string | null;
}

interface LaneController {
  start: (stream: MediaStream) => Promise<void>;
  stop: () => Promise<void>;
  onVoice: (at: number, level: number) => void;
  clear: () => void;
}

interface LaneProps {
  id: ProviderId;
  provider: ProviderStatus;
  sourceLanguage: LanguageCode;
  targetLanguage: LanguageCode;
  register: (id: ProviderId, controller: LaneController | null) => void;
  reportState: (id: ProviderId, state: SessionState) => void;
  recordTimelineEvent: (id: ProviderId, event: TimelineEventDraft) => void;
}

interface PendingCaption {
  text: string;
  epoch: number;
  receivedAt: number;
}

interface TimelineEventDraft {
  at: number;
  latencyMs?: number;
  text: string;
  kind: TimelineEventKind;
}

const outputLanguages = languages.filter((language) => language.code !== "auto");
const PROVISIONAL_PAINT_INTERVAL_MS = 320;
const providerOrder: ProviderId[] = [
  "scribe-groq",
  "assemblyai-fast",
  "assemblyai-accurate",
  "assemblyai-wide",
  "openai-live-groq",
  "openai",
  "soniox",
  "gemini",
  "gradium",
];
const providerLabels: Record<ProviderId, string> = {
  "scribe-groq": "Scribe + Groq",
  "assemblyai-fast": "AssemblyAI · Fast",
  "assemblyai-accurate": "AssemblyAI · 3.5 Pro",
  "assemblyai-wide": "AssemblyAI · Wide coverage",
  "openai-live-groq": "OpenAI Live + Groq",
  openai: "OpenAI Direct Translate",
  soniox: "Soniox Realtime",
  gemini: "Gemini Live Translate",
  gradium: "Gradium Live Translate",
};
const assemblyAIModes: Partial<Record<ProviderId, AssemblyAIMode>> = {
  "assemblyai-fast": "fast",
  "assemblyai-accurate": "accurate",
  "assemblyai-wide": "wide",
};

const emptyLaneState = (): LaneState => ({
  sessionState: "idle",
  settledSource: [],
  sourcePreview: "",
  captionRows: [],
  captionPreview: emptyCaptionRevision(),
  metrics: { revisionCount: 0 },
  error: null,
});

const supportsRoute = (
  provider: ProviderStatus | undefined,
  sourceLanguage: LanguageCode,
  targetLanguage: LanguageCode,
): boolean => Boolean(
  provider?.ready
  && (!provider.supportedSources || provider.supportedSources.includes(sourceLanguage))
  && (!provider.supportedTargets || provider.supportedTargets.includes(targetLanguage)),
);

const formatMs = (value?: number): string =>
  value === undefined ? "Awaiting" : `${Math.max(0, Math.round(value))} ms`;

const timelineFragment = (previous: string, next: string): string => {
  const cleanPrevious = previous.trim();
  const cleanNext = next.trim();
  if (!cleanNext) return "";
  const addition = cleanPrevious && cleanNext.startsWith(cleanPrevious)
    ? cleanNext.slice(cleanPrevious.length).trim()
    : cleanNext;
  const words = (addition || cleanNext).split(/\s+/).filter(Boolean);
  return words.slice(-4).join(" ");
};

function useLaneEngine(
  id: ProviderId,
  sourceLanguage: LanguageCode,
  targetLanguage: LanguageCode,
  recordTimelineEvent: LaneProps["recordTimelineEvent"],
) {
  const [state, setState] = useState<LaneState>(emptyLaneState);
  const timing = useRef({
    utteranceStartedAt: null as number | null,
    speechEndedAt: null as number | null,
    firstPartialAt: null as number | null,
    firstCaptionAt: null as number | null,
    lastVoiceAt: null as number | null,
  });
  const voiceEvidence = useRef<VoiceEvidence>({ activeMs: 0, frames: 0, peak: 0 });
  const captionEpoch = useRef(0);
  const provisionalFull = useRef("");
  const provisionalAbort = useRef<AbortController | null>(null);
  const provisionalTimer = useRef<number | null>(null);
  const paintTimer = useRef<number | null>(null);
  const pendingCaption = useRef<PendingCaption | null>(null);
  const lastPaintAt = useRef(0);
  const lastPartial = useRef("");
  const lastCommit = useRef<{ text: string; at: number } | null>(null);
  const finalQueue = useRef<Promise<void>>(Promise.resolve());
  const lastNativeTranslation = useRef("");
  const lastTimelineText = useRef("");
  const timelineHasFirst = useRef(false);

  const patchState = useCallback((patch: Partial<LaneState>) => {
    setState((current) => ({ ...current, ...patch }));
  }, []);

  const cancelProvisional = useCallback(() => {
    if (provisionalTimer.current !== null) window.clearTimeout(provisionalTimer.current);
    if (paintTimer.current !== null) window.clearTimeout(paintTimer.current);
    provisionalTimer.current = null;
    paintTimer.current = null;
    pendingCaption.current = null;
    provisionalAbort.current?.abort();
    provisionalAbort.current = null;
  }, []);

  const clearPreview = useCallback(() => {
    provisionalFull.current = "";
    lastPaintAt.current = 0;
    patchState({ captionPreview: emptyCaptionRevision() });
  }, [patchState]);

  const paintPending = useCallback(() => {
    paintTimer.current = null;
    const candidate = pendingCaption.current;
    pendingCaption.current = null;
    if (!candidate || candidate.epoch !== captionEpoch.current) return;
    const revision = splitStableCaption(provisionalFull.current, candidate.text);
    if (!revision.full || revision.full === provisionalFull.current) return;
    const previousTimelineText = lastTimelineText.current;
    provisionalFull.current = revision.full;
    lastPaintAt.current = performance.now();
    const firstTimelineEvent = !timelineHasFirst.current;
    const fragment = timelineFragment(previousTimelineText, revision.full);
    if (fragment) {
      recordTimelineEvent(id, {
        at: lastPaintAt.current,
        latencyMs: firstTimelineEvent
          ? lastPaintAt.current - (timing.current.utteranceStartedAt ?? candidate.receivedAt)
          : undefined,
        text: fragment,
        kind: firstTimelineEvent ? "first" : "update",
      });
      timelineHasFirst.current = true;
      lastTimelineText.current = revision.full;
    }
    setState((current) => ({
      ...current,
      captionPreview: revision,
      metrics: {
        ...current.metrics,
        firstTranslatedTextMs: current.metrics.firstTranslatedTextMs
          ?? lastPaintAt.current - (timing.current.utteranceStartedAt ?? candidate.receivedAt),
      },
    }));
    timing.current.firstCaptionAt ??= lastPaintAt.current;
  }, [id, recordTimelineEvent]);

  const queueCaption = useCallback((candidate: PendingCaption) => {
    if (!candidate.text || candidate.epoch !== captionEpoch.current) return;
    pendingCaption.current = candidate;
    if (paintTimer.current !== null) return;
    const wait = Math.max(0, PROVISIONAL_PAINT_INTERVAL_MS - (performance.now() - lastPaintAt.current));
    paintTimer.current = window.setTimeout(paintPending, wait);
  }, [paintPending]);

  const translateProvisional = useCallback(async (
    text: string,
    epoch: number,
    receivedAt: number,
  ) => {
    const controller = new AbortController();
    provisionalAbort.current = controller;
    let translation = "";
    try {
      await streamTranslation({
        text,
        sourceLanguage,
        targetLanguage,
        mode: "provisional",
        signal: controller.signal,
        onToken: (token) => {
          if (epoch !== captionEpoch.current) return;
          translation += token;
          const readable = completeWordsOnly(translation);
          if (readable) queueCaption({ text: readable, epoch, receivedAt });
        },
      });
      if (epoch === captionEpoch.current && translation.trim()) {
        queueCaption({ text: translation.trim(), epoch, receivedAt });
      }
    } catch (cause) {
      if (controller.signal.aborted) return;
      patchState({ error: cause instanceof Error ? cause.message : "The live preview could not be translated." });
    }
  }, [patchState, queueCaption, sourceLanguage, targetLanguage]);

  const acceptPartial = useCallback((text: string) => {
    const clean = text.trim();
    if (!clean || clean === lastPartial.current) return;
    lastPartial.current = clean;
    const now = performance.now();
    timing.current.firstPartialAt ??= now;
    cancelProvisional();
    const epoch = ++captionEpoch.current;
    setState((current) => ({
      ...current,
      sourcePreview: clean,
      metrics: {
        ...current.metrics,
        firstSourcePartialMs: current.metrics.firstSourcePartialMs
          ?? now - (timing.current.utteranceStartedAt ?? now),
        revisionCount: current.metrics.revisionCount + 1,
      },
    }));
    provisionalTimer.current = window.setTimeout(() => {
      provisionalTimer.current = null;
      void translateProvisional(clean, epoch, now);
    }, 110);
  }, [cancelProvisional, translateProvisional]);

  const resetUtterance = useCallback(() => {
    timing.current.utteranceStartedAt = null;
    timing.current.speechEndedAt = null;
    timing.current.firstPartialAt = null;
    timing.current.firstCaptionAt = null;
    timing.current.lastVoiceAt = null;
    lastPartial.current = "";
    resetVoiceEvidence(voiceEvidence.current);
  }, []);

  const acceptCommitted = useCallback((
    text: string,
    _detectedLanguage?: string,
    words?: WordTimestamp[],
  ) => {
    const clean = text.trim();
    if (!clean) return;
    const committedAt = performance.now();
    if (lastCommit.current?.text === clean && committedAt - lastCommit.current.at < 1_000) return;
    cancelProvisional();
    ++captionEpoch.current;
    if (isLikelySilenceArtifact(clean, voiceEvidence.current, words)) {
      patchState({ sourcePreview: "" });
      clearPreview();
      resetUtterance();
      return;
    }
    lastCommit.current = { text: clean, at: committedAt };
    const rowId = crypto.randomUUID();
    const speechEndedAt = timing.current.speechEndedAt ?? committedAt;
    const utteranceStartedAt = timing.current.utteranceStartedAt ?? committedAt;
    const previewSnapshot = provisionalFull.current;
    setState((current) => ({
      ...current,
      settledSource: [...current.settledSource, clean],
      sourcePreview: "",
      captionRows: [...current.captionRows, { id: rowId, text: previewSnapshot, status: "settling" }],
      captionPreview: emptyCaptionRevision(),
      metrics: {
        ...current.metrics,
        committedSourceMs: committedAt - (timing.current.utteranceStartedAt ?? committedAt),
      },
    }));
    provisionalFull.current = "";
    resetUtterance();

    finalQueue.current = finalQueue.current.then(async () => {
      let translation = "";
      try {
        await streamTranslation({
          text: clean,
          sourceLanguage,
          targetLanguage,
          mode: "committed",
          onToken: (token) => {
            translation += token;
          },
        });
        const settledAt = performance.now();
        const cleanTranslation = translation.trim();
        const firstTimelineEvent = !timelineHasFirst.current;
        const fragment = timelineFragment(lastTimelineText.current, cleanTranslation);
        if (fragment) {
          recordTimelineEvent(id, {
            at: settledAt,
            latencyMs: firstTimelineEvent ? settledAt - utteranceStartedAt : undefined,
            text: fragment,
            kind: firstTimelineEvent ? "first" : "final",
          });
          timelineHasFirst.current = true;
          lastTimelineText.current = cleanTranslation;
        }
        setState((current) => ({
          ...current,
          captionRows: current.captionRows.map((row) => row.id === rowId
            ? { ...row, text: cleanTranslation, status: "complete" }
            : row),
          metrics: {
            ...current.metrics,
            firstTranslatedTextMs: current.metrics.firstTranslatedTextMs
              ?? settledAt - utteranceStartedAt,
            finalCaptionAfterSpeechMs: settledAt - speechEndedAt,
          },
        }));
      } catch (cause) {
        const message = cause instanceof Error ? cause.message : "The final caption could not be translated.";
        setState((current) => ({
          ...current,
          captionRows: current.captionRows.map((row) => row.id === rowId
            ? { ...row, status: "error" }
            : row),
          error: message,
        }));
      }
    });
  }, [
    cancelProvisional,
    clearPreview,
    id,
    patchState,
    recordTimelineEvent,
    resetUtterance,
    sourceLanguage,
    targetLanguage,
  ]);

  const acceptNativeSnapshot = useCallback((snapshot: NativeCaptionSnapshot) => {
    const now = performance.now();
    const translation = [snapshot.translationSettled, snapshot.translationPreview].filter(Boolean).join(" ");
    const source = [snapshot.sourceSettled, snapshot.sourcePreview].filter(Boolean).join(" ");
    const translationChanged = Boolean(translation && translation !== lastNativeTranslation.current);
    if (translationChanged) lastNativeTranslation.current = translation;
    if (translationChanged) {
      const firstTimelineEvent = !timelineHasFirst.current;
      const fragment = timelineFragment(lastTimelineText.current, translation);
      if (fragment) {
        recordTimelineEvent(id, {
          at: now,
          latencyMs: firstTimelineEvent
            ? now - (timing.current.utteranceStartedAt ?? now)
            : undefined,
          text: fragment,
          kind: firstTimelineEvent
            ? "first"
            : snapshot.translationSettled && !snapshot.translationPreview ? "final" : "update",
        });
        timelineHasFirst.current = true;
        lastTimelineText.current = translation;
      }
    }
    const preview = snapshot.translationPreview
      ? splitStableCaption(provisionalFull.current, snapshot.translationPreview)
      : emptyCaptionRevision();
    provisionalFull.current = preview.full;
    setState((current) => ({
      ...current,
      settledSource: snapshot.sourceSettled ? [snapshot.sourceSettled] : current.settledSource,
      sourcePreview: snapshot.sourcePreview,
      captionRows: snapshot.translationSettled
        ? [{ id: `${id}-native`, text: snapshot.translationSettled, status: "complete" }]
        : current.captionRows,
      captionPreview: preview,
      metrics: {
        ...current.metrics,
        firstSourcePartialMs: source
          ? current.metrics.firstSourcePartialMs ?? now - (timing.current.utteranceStartedAt ?? now)
          : current.metrics.firstSourcePartialMs,
        firstTranslatedTextMs: translation
          ? current.metrics.firstTranslatedTextMs ?? now - (timing.current.utteranceStartedAt ?? now)
          : current.metrics.firstTranslatedTextMs,
        finalCaptionAfterSpeechMs: snapshot.translationSettled && !snapshot.translationPreview
          ? now - (timing.current.speechEndedAt ?? now)
          : current.metrics.finalCaptionAfterSpeechMs,
        revisionCount: current.metrics.revisionCount + (translationChanged ? 1 : 0),
      },
    }));
  }, [id, recordTimelineEvent]);

  const onVoice = useCallback((at: number, level: number) => {
    if (timing.current.utteranceStartedAt === null) {
      timing.current.utteranceStartedAt = at;
      timing.current.firstPartialAt = null;
      timing.current.firstCaptionAt = null;
      timing.current.lastVoiceAt = null;
      resetVoiceEvidence(voiceEvidence.current);
      setState((current) => ({
        ...current,
        metrics: { revisionCount: current.metrics.revisionCount },
      }));
    }
    const previous = timing.current.lastVoiceAt;
    voiceEvidence.current.frames += 1;
    voiceEvidence.current.activeMs += previous !== null && at - previous < 100 ? at - previous : 40;
    voiceEvidence.current.peak = Math.max(voiceEvidence.current.peak, level);
    timing.current.lastVoiceAt = at;
    timing.current.speechEndedAt = at;
  }, []);

  const prepareStart = useCallback(() => {
    timelineHasFirst.current = false;
    lastTimelineText.current = "";
    patchState({ sessionState: "starting", error: null });
  }, [patchState]);

  const markListening = useCallback(() => {
    patchState({ sessionState: "listening" });
  }, [patchState]);

  const markStopped = useCallback(() => {
    cancelProvisional();
    patchState({ sessionState: "idle" });
    resetUtterance();
  }, [cancelProvisional, patchState, resetUtterance]);

  const fail = useCallback((message: string) => {
    patchState({ sessionState: "error", error: message });
  }, [patchState]);

  const clear = useCallback(() => {
    cancelProvisional();
    provisionalFull.current = "";
    lastNativeTranslation.current = "";
    lastTimelineText.current = "";
    timelineHasFirst.current = false;
    setState((current) => ({ ...emptyLaneState(), sessionState: current.sessionState }));
  }, [cancelProvisional]);

  useEffect(() => () => cancelProvisional(), [cancelProvisional]);

  return {
    state,
    acceptPartial,
    acceptCommitted,
    acceptNativeSnapshot,
    onVoice,
    prepareStart,
    markListening,
    markStopped,
    fail,
    clear,
  };
}

function LaneView({ id, provider, state, targetLanguage }: {
  id: ProviderId;
  provider: ProviderStatus;
  state: LaneState;
  targetLanguage: LanguageCode;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const hasCaption = state.captionRows.some((row) => Boolean(row.text)) || Boolean(state.captionPreview.full);
  const fastest = state.metrics.firstTranslatedTextMs;

  useEffect(() => {
    const surface = scrollRef.current;
    if (surface) surface.scrollTop = surface.scrollHeight;
  }, [state.captionRows, state.captionPreview.full]);

  return (
    <article className={`provider-lane provider-lane--${state.sessionState}`} aria-labelledby={`${id}-title`}>
      <header className="provider-lane__header">
        <div className="provider-lane__identity">
          <span className="provider-lane__signal" aria-hidden="true" />
          <span>
            <h2 id={`${id}-title`}>{providerLabels[id]}</h2>
            <span className="provider-lane__path">{provider.input}</span>
          </span>
        </div>
        <div className="provider-lane__first">
          <span>First text</span>
          <strong>{formatMs(fastest)}</strong>
        </div>
      </header>

      <div className="provider-lane__caption" ref={scrollRef} aria-live="polite" aria-atomic="false">
        <p className={hasCaption ? "" : "provider-lane__empty"} dir={targetLanguage === "ar" ? "rtl" : "auto"}>
          {hasCaption ? (
            <>
              {state.captionRows.map((row) => row.text && (
                <span className={`settled-phrase settled-phrase--${row.status}`} key={row.id}>{row.text}</span>
              ))}
              {state.captionPreview.full && (
                <span className="live-phrase">
                  {state.captionPreview.stable && <span className="live-phrase__stable">{state.captionPreview.stable}</span>}
                  {state.captionPreview.stable && state.captionPreview.changing ? " " : null}
                  {state.captionPreview.changing && <span>{state.captionPreview.changing}</span>}
                </span>
              )}
            </>
          ) : state.sessionState === "listening"
            ? "Listening for translated words…"
            : "Ready for a comparison run."}
        </p>
      </div>

      <div className="provider-lane__source">
        <span>Source</span>
        <p>
          {state.settledSource.map((text, index) => <span key={`${id}-${index}-${text}`}>{text}</span>)}
          {state.sourcePreview && <span className="source-preview">{state.sourcePreview}</span>}
          {!state.settledSource.length && !state.sourcePreview && "Waiting for speech"}
        </p>
      </div>

      <footer className="provider-lane__footer">
        {state.error ? (
          <p className="provider-lane__error" role="alert">{state.error}</p>
        ) : (
          <dl className="lane-metrics">
            <div><dt>Final settle</dt><dd>{formatMs(state.metrics.finalCaptionAfterSpeechMs)}</dd></div>
            <div><dt>Changes</dt><dd>{state.metrics.revisionCount}</dd></div>
          </dl>
        )}
      </footer>
    </article>
  );
}

function ScribeProviderLane(props: LaneProps) {
  const engine = useLaneEngine(
    props.id,
    props.sourceLanguage,
    props.targetLanguage,
    props.recordTimelineEvent,
  );
  const intentionalStop = useRef(false);
  const scribe = useScribe({
    modelId: "scribe_v2_realtime",
    commitStrategy: CommitStrategy.VAD,
    vadSilenceThresholdSecs: 0.5,
    vadThreshold: 0.48,
    minSpeechDurationMs: 180,
    minSilenceDurationMs: 120,
    includeTimestamps: true,
    includeLanguageDetection: props.sourceLanguage === "auto",
    languageCode: props.sourceLanguage === "auto" ? undefined : props.sourceLanguage,
    onPartialTranscript: ({ text }) => engine.acceptPartial(text),
    onCommittedTranscriptWithTimestamps: ({ text, language_code, words }) => {
      engine.acceptCommitted(text, language_code, words);
    },
    onError: (event) => {
      if (intentionalStop.current) return;
      engine.fail(event instanceof Error ? event.message : "Scribe lost its microphone stream.");
    },
  });
  const scribeRef = useRef(scribe);
  scribeRef.current = scribe;

  useEffect(() => {
    props.reportState(props.id, engine.state.sessionState);
  }, [engine.state.sessionState, props.id, props.reportState]);

  useEffect(() => {
    const controller: LaneController = {
      start: async () => {
        engine.prepareStart();
        intentionalStop.current = false;
        try {
          const token = await getElevenLabsToken("realtime_scribe");
          await scribeRef.current.connect({
            token,
            modelId: "scribe_v2_realtime",
            commitStrategy: CommitStrategy.VAD,
            vadSilenceThresholdSecs: 0.5,
            vadThreshold: 0.48,
            minSpeechDurationMs: 180,
            minSilenceDurationMs: 120,
            includeTimestamps: true,
            includeLanguageDetection: props.sourceLanguage === "auto",
            languageCode: props.sourceLanguage === "auto" ? undefined : props.sourceLanguage,
            microphone: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: false,
              channelCount: 1,
            },
          });
          engine.markListening();
        } catch (cause) {
          const message = cause instanceof Error ? cause.message : "Scribe could not start.";
          engine.fail(message);
          throw cause;
        }
      },
      stop: async () => {
        intentionalStop.current = true;
        scribeRef.current.disconnect();
        engine.markStopped();
      },
      onVoice: engine.onVoice,
      clear: engine.clear,
    };
    props.register(props.id, controller);
    return () => props.register(props.id, null);
  }, [
    engine.clear,
    engine.fail,
    engine.markListening,
    engine.markStopped,
    engine.onVoice,
    engine.prepareStart,
    props.id,
    props.register,
    props.sourceLanguage,
  ]);

  return <LaneView id={props.id} provider={props.provider} state={engine.state} targetLanguage={props.targetLanguage} />;
}

function NativeProviderLane(props: LaneProps) {
  const engine = useLaneEngine(
    props.id,
    props.sourceLanguage,
    props.targetLanguage,
    props.recordTimelineEvent,
  );
  const sessionRef = useRef<NativeTranslationSession | null>(null);

  useEffect(() => {
    props.reportState(props.id, engine.state.sessionState);
  }, [engine.state.sessionState, props.id, props.reportState]);

  useEffect(() => {
    const controller: LaneController = {
      start: async (stream) => {
        engine.prepareStart();
        const fail = (message: string) => engine.fail(message);
        const assemblyMode = assemblyAIModes[props.id];
        const session: NativeTranslationSession = assemblyMode
          ? new AssemblyAITranscriptionSession(assemblyMode, {
              onPartial: engine.acceptPartial,
              onCommitted: engine.acceptCommitted,
              onError: fail,
            })
          : props.id === "openai-live-groq"
            ? new OpenAITranscriptionSession(props.sourceLanguage, {
                onPartial: engine.acceptPartial,
                onCommitted: engine.acceptCommitted,
                onError: fail,
              })
            : props.id === "openai"
              ? new OpenAITranslationSession(props.targetLanguage, {
                  onSnapshot: engine.acceptNativeSnapshot,
                  onError: fail,
                })
              : props.id === "soniox"
                ? new SonioxTranslationSession(props.sourceLanguage, props.targetLanguage, {
                    onSnapshot: engine.acceptNativeSnapshot,
                    onError: fail,
                  })
                : props.id === "gemini"
                  ? new GeminiTranslationSession(props.targetLanguage, {
                      onSnapshot: engine.acceptNativeSnapshot,
                      onError: fail,
                    })
                  : new GradiumTranslationSession(props.targetLanguage, {
                      onSnapshot: engine.acceptNativeSnapshot,
                      onError: fail,
                    });
        sessionRef.current = session;
        try {
          await session.start(stream);
          engine.markListening();
        } catch (cause) {
          await session.stop().catch(() => undefined);
          sessionRef.current = null;
          const message = cause instanceof Error ? cause.message : `${providerLabels[props.id]} could not start.`;
          engine.fail(message);
          throw cause;
        }
      },
      stop: async () => {
        await sessionRef.current?.stop();
        sessionRef.current = null;
        engine.markStopped();
      },
      onVoice: engine.onVoice,
      clear: engine.clear,
    };
    props.register(props.id, controller);
    return () => {
      props.register(props.id, null);
      void sessionRef.current?.stop().catch(() => undefined);
      sessionRef.current = null;
    };
  }, [
    engine.acceptCommitted,
    engine.acceptNativeSnapshot,
    engine.acceptPartial,
    engine.clear,
    engine.fail,
    engine.markListening,
    engine.markStopped,
    engine.onVoice,
    engine.prepareStart,
    props.id,
    props.register,
    props.sourceLanguage,
    props.targetLanguage,
  ]);

  return <LaneView id={props.id} provider={props.provider} state={engine.state} targetLanguage={props.targetLanguage} />;
}

export function App() {
  const [sourceLanguage, setSourceLanguage] = useState<LanguageCode>("en");
  const [targetLanguage, setTargetLanguage] = useState<LanguageCode>("fr");
  const [stackStatus, setStackStatus] = useState<StackStatus | null>(null);
  const [selected, setSelected] = useState<ProviderId[]>([]);
  const [sessionState, setSessionState] = useState<SessionState>("idle");
  const [laneStates, setLaneStates] = useState<Partial<Record<ProviderId, SessionState>>>({});
  const [timelineEvents, setTimelineEvents] = useState<Partial<Record<ProviderId, TimelineEvent[]>>>({});
  const [timelineStartedAt, setTimelineStartedAt] = useState<number | null>(null);
  const [timelineEndedAtMs, setTimelineEndedAtMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const controllers = useRef(new Map<ProviderId, LaneController>());
  const activeIds = useRef<ProviderId[]>([]);
  const selectionReady = useRef(false);
  const timelineStartedAtRef = useRef<number | null>(null);
  const timelineSequence = useRef(0);

  const recordTimelineEvent = useCallback((id: ProviderId, draft: TimelineEventDraft) => {
    const startedAt = timelineStartedAtRef.current;
    if (startedAt === null || !draft.text) return;
    const event: TimelineEvent = {
      id: ++timelineSequence.current,
      atMs: Math.max(0, draft.at - startedAt),
      latencyMs: draft.latencyMs,
      text: draft.text,
      kind: draft.kind,
    };
    setTimelineEvents((current) => ({
      ...current,
      [id]: [...(current[id] ?? []), event].slice(-96),
    }));
  }, []);

  const onVoice = useCallback((at: number, level: number) => {
    for (const id of activeIds.current) controllers.current.get(id)?.onVoice(at, level);
  }, []);
  const meter = useAudioMeter(onVoice);

  const register = useCallback((id: ProviderId, controller: LaneController | null) => {
    if (controller) controllers.current.set(id, controller);
    else controllers.current.delete(id);
  }, []);

  const reportState = useCallback((id: ProviderId, state: SessionState) => {
    setLaneStates((current) => current[id] === state ? current : { ...current, [id]: state });
  }, []);

  const refreshStackStatus = useCallback(async () => {
    try {
      const status = await getStackStatus();
      setStackStatus(status);
      if (selectionReady.current) return;
      selectionReady.current = true;
      const saved = localStorage.getItem("translator-selected-providers");
      if (saved) {
        try {
          const parsed = JSON.parse(saved) as string[];
          setSelected(providerOrder.filter((id) => parsed.includes(id)));
          return;
        } catch {
          localStorage.removeItem("translator-selected-providers");
        }
      }
      setSelected(providerOrder.filter((id) => status.providers[id]?.ready));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not reach the translation service.");
    }
  }, []);

  useEffect(() => {
    void refreshStackStatus();
    const refresh = () => {
      if (!document.hidden) void refreshStackStatus();
    };
    const interval = window.setInterval(refresh, 15_000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [refreshStackStatus]);

  useEffect(() => {
    if (!selectionReady.current) return;
    localStorage.setItem("translator-selected-providers", JSON.stringify(selected));
  }, [selected]);

  const supportedSelected = useMemo(() => selected.filter((id) =>
    supportsRoute(stackStatus?.providers[id], sourceLanguage, targetLanguage)),
  [selected, sourceLanguage, stackStatus, targetLanguage]);

  const toggleProvider = (id: ProviderId) => {
    if (sessionState !== "idle" && sessionState !== "error") return;
    setSelected((current) => current.includes(id)
      ? current.filter((providerId) => providerId !== id)
      : providerOrder.filter((providerId) => providerId === id || current.includes(providerId)));
  };

  const startSession = useCallback(async () => {
    if (!supportedSelected.length) return;
    setSessionState("starting");
    setError(null);
    timelineStartedAtRef.current = null;
    setTimelineStartedAt(null);
    setTimelineEndedAtMs(null);
    setTimelineEvents({});
    try {
      const stream = await meter.start();
      const startedAt = performance.now();
      timelineStartedAtRef.current = startedAt;
      timelineSequence.current = 0;
      setTimelineStartedAt(startedAt);
      setTimelineEndedAtMs(null);
      setTimelineEvents({});
      activeIds.current = supportedSelected;
      const results = await Promise.allSettled(supportedSelected.map(async (id) => {
        const controller = controllers.current.get(id);
        if (!controller) throw new Error(`${providerLabels[id]} is still loading.`);
        await controller.start(stream);
      }));
      const started = results.filter((result) => result.status === "fulfilled").length;
      if (!started) {
        setTimelineEndedAtMs(performance.now() - startedAt);
        meter.stop();
        activeIds.current = [];
        setSessionState("error");
        setError("None of the selected providers could start.");
        return;
      }
      setSessionState("listening");
      if (started < supportedSelected.length) {
        setError(`${started} of ${supportedSelected.length} providers started. Failed rows are marked below.`);
      }
    } catch (cause) {
      const startedAt = timelineStartedAtRef.current;
      if (startedAt !== null) setTimelineEndedAtMs(performance.now() - startedAt);
      meter.stop();
      activeIds.current = [];
      setSessionState("error");
      setError(cause instanceof Error ? cause.message : "The comparison run could not start.");
    }
  }, [meter, supportedSelected]);

  const stopSession = useCallback(async () => {
    setSessionState("stopping");
    const ids = [...activeIds.current];
    await Promise.allSettled(ids.map((id) => controllers.current.get(id)?.stop()));
    activeIds.current = [];
    meter.stop();
    const startedAt = timelineStartedAtRef.current;
    if (startedAt !== null) setTimelineEndedAtMs(performance.now() - startedAt);
    setSessionState("idle");
  }, [meter]);

  const clearAll = () => {
    for (const id of selected) controllers.current.get(id)?.clear();
    timelineStartedAtRef.current = null;
    timelineSequence.current = 0;
    setTimelineStartedAt(null);
    setTimelineEndedAtMs(null);
    setTimelineEvents({});
    setError(null);
  };

  const swapLanguages = () => {
    if (sourceLanguage === "auto" || sessionState === "listening") return;
    setSourceLanguage(targetLanguage);
    setTargetLanguage(sourceLanguage);
  };

  const isListening = sessionState === "listening";
  const isBusy = sessionState === "starting" || sessionState === "stopping";
  const unavailableSelected = selected.length - supportedSelected.length;
  const liveCount = activeIds.current.filter((id) => laneStates[id] === "listening").length;
  const overallStatus = isListening
    ? liveCount === activeIds.current.length
      ? `${liveCount} providers live`
      : `${liveCount} of ${activeIds.current.length} providers live`
    : isBusy
      ? sessionState === "starting" ? "Connecting providers" : "Stopping"
      : supportedSelected.length
        ? `${supportedSelected.length} selected`
        : "Select a provider";
  const timelineRows = selected.map((id) => ({
    id,
    label: providerLabels[id],
    events: timelineEvents[id] ?? [],
  }));

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand" aria-label="Translator glasses latency lab">
          <span className="brand__signal" aria-hidden="true"><i /><i /><i /><i /><i /></span>
          <span>Translator</span>
          <span className="brand__mode">Glasses latency lab</span>
        </div>
        <div className={`connection-state connection-state--${isListening ? "listening" : "ready"}`}>
          <span className="connection-state__mark" aria-hidden="true" />
          {overallStatus}
        </div>
      </header>

      <section className="control-deck" aria-label="Comparison controls">
        <div className="route-controls">
          <label className="language-control">
            <span>Speaker</span>
            <select value={sourceLanguage} onChange={(event) => setSourceLanguage(event.target.value as LanguageCode)} disabled={isListening || isBusy}>
              {languages.map((language) => <option key={language.code} value={language.code}>{language.label}</option>)}
            </select>
          </label>
          <button className="swap-control" type="button" onClick={swapLanguages} disabled={sourceLanguage === "auto" || isListening || isBusy} aria-label="Swap languages">
            <SwapMark />
          </button>
          <label className="language-control">
            <span>Caption</span>
            <select value={targetLanguage} onChange={(event) => setTargetLanguage(event.target.value as LanguageCode)} disabled={isListening || isBusy}>
              {outputLanguages.map((language) => <option key={language.code} value={language.code}>{language.label}</option>)}
            </select>
          </label>
        </div>

        <div className="microphone-monitor">
          <Waveform level={meter.level} active={isListening} />
          <span>{isListening ? "Shared microphone live" : "One microphone feeds every selected row"}</span>
        </div>

        <div className="run-controls">
          <button
            type="button"
            className={`session-control ${isListening ? "session-control--active" : ""}`}
            onClick={() => void (isListening ? stopSession() : startSession())}
            disabled={(!supportedSelected.length || isBusy) && !isListening}
          >
            {isListening ? <StopMark /> : <MicMark />}
            <span>{isListening ? "Stop comparison" : isBusy ? "Connecting" : "Start comparison"}</span>
          </button>
          <button className="clear-control" type="button" onClick={clearAll} disabled={isListening || isBusy}>Clear text</button>
        </div>
      </section>

      <section className="provider-picker" aria-labelledby="provider-picker-title">
        <div className="provider-picker__heading">
          <h1 id="provider-picker-title">Choose the race</h1>
          <p>All checked providers listen at once. Uncheck the clear losers before the next run.</p>
        </div>
        <div className="provider-options">
          {providerOrder.map((id) => {
            const provider = stackStatus?.providers[id];
            const routeSupported = supportsRoute(provider, sourceLanguage, targetLanguage);
            const routeMismatch = provider?.ready && !routeSupported;
            const disabled = isListening || isBusy || !provider?.ready || routeMismatch;
            return (
              <label className={`provider-option ${selected.includes(id) ? "provider-option--selected" : ""} ${disabled ? "provider-option--disabled" : ""}`} key={id}>
                <input
                  type="checkbox"
                  checked={selected.includes(id)}
                  onChange={() => toggleProvider(id)}
                  disabled={disabled}
                />
                <span className="provider-option__check" aria-hidden="true" />
                <span>
                  <strong>{providerLabels[id]}</strong>
                  <small>{!provider ? "Checking"
                    : !provider.ready ? "Needs API key"
                      : routeMismatch ? "Language not supported"
                        : provider.preview}</small>
                </span>
              </label>
            );
          })}
        </div>
        {unavailableSelected > 0 && (
          <p className="selection-note">{unavailableSelected} saved choice{unavailableSelected === 1 ? "" : "s"} cannot run with the current keys or languages.</p>
        )}
      </section>

      {error && <p className="global-error" role="alert">{error}</p>}

      <section className="comparison-board" aria-label="Live provider comparison">
        {selected.length ? selected.map((id) => {
          const provider = stackStatus?.providers[id];
          if (!provider) return null;
          const laneProps: LaneProps = {
            id,
            provider,
            sourceLanguage,
            targetLanguage,
            register,
            reportState,
            recordTimelineEvent,
          };
          return id === "scribe-groq"
            ? <ScribeProviderLane key={id} {...laneProps} />
            : <NativeProviderLane key={id} {...laneProps} />;
        }) : (
          <div className="empty-board">
            <strong>No providers selected</strong>
            <span>Choose at least one option above to begin the comparison.</span>
          </div>
        )}
      </section>

      {selected.length > 0 && (
        <LatencyTimeline
          rows={timelineRows}
          startedAt={timelineStartedAt}
          endedAtMs={timelineEndedAtMs}
          running={isListening || sessionState === "starting"}
        />
      )}

      <footer className="bench-note">
        <span>Muted text is still changing. Bright text is settled and stays in the conversation.</span>
        <span>Text only · no audio playback</span>
      </footer>
    </main>
  );
}
